/**
 * Teams-style presence dot for the Felix avatar.
 *
 * Polls /felix/health-status every 60s and renders a tiny coloured
 * circle in the bottom-right corner of the avatar.  Hovering shows a
 * structured tooltip with the underlying signals (Anthropic
 * indicator + FTL error rate).  Green = operational, amber = degraded,
 * red = outage, grey = unknown.
 *
 * The endpoint is auth-gated so the dot is only useful for logged-in
 * users; place it inside the FelixChat header (where it is already
 * mounted behind an auth boundary).
 */
import { useQuery } from "@tanstack/react-query";
import { assistantApi, type FelixHealthStatus } from "@/api/services";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<
  FelixHealthStatus["status"],
  { color: string; label: string }
> = {
  operational: {
    color: "bg-green-500 ring-green-500/30",
    label: "Operational",
  },
  degraded: {
    color: "bg-amber-500 ring-amber-500/30",
    label: "Degraded",
  },
  outage: {
    color: "bg-red-500 ring-red-500/30",
    label: "Outage",
  },
  unknown: {
    color: "bg-slate-400 ring-slate-400/30",
    label: "Unknown",
  },
};

export function FelixHealthDot({ className }: { className?: string }) {
  const { data } = useQuery({
    queryKey: ["felix-health-status"],
    queryFn: () => assistantApi.healthStatus(),
    // 60s poll — endpoint is cheap (cached 30s server-side) but we
    // don't need real-time precision for a presence dot.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // If the endpoint is unreachable, fail soft to "unknown" rather
    // than throwing into the chat surface.
    retry: 1,
    staleTime: 30_000,
  });

  const status = data?.status ?? "unknown";
  const style = STATUS_STYLES[status];
  const tooltipLines = data
    ? [
        `Felix: ${style.label}`,
        data.message,
        `Anthropic: ${data.anthropic.indicator || "unknown"}`,
        `FTL 24h errors: ${data.ftl.errored_sessions}/${data.ftl.total_sessions} (${(
          data.ftl.error_rate_24h * 100
        ).toFixed(1)}%)`,
      ]
    : ["Felix: Unknown", "Status indicator unavailable."];

  return (
    <span
      role="status"
      aria-label={`Felix is ${style.label.toLowerCase()}`}
      title={tooltipLines.join("\n")}
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full ring-2 transition-colors",
        style.color,
        className,
      )}
    />
  );
}

export default FelixHealthDot;
